import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import useAxios from '../../hooks/useAxios';
import { User } from '../../Models/User';
import { ADMIN_PROJECT_PARTICIPANTS_URL } from '../../utils/constant';
import { ShareConfiguration } from '../../interface/UserProject.interface';

interface ParticipantData {
  uuid: string;
  email: string;
  firstname: string;
  lastname: string;
  role: string;
  isActivated: boolean;
  createdAt?: string;
  totalShare?: number;
  shareConfigurations?: ShareConfiguration[];
  upToDate?: boolean;
}

function mapParticipantsToUsers(participants: ParticipantData[], projectId: string): User[] {
  return participants.map((participant) =>
    new User({
      email: participant.email,
      firstname: participant.firstname,
      lastname: participant.lastname,
      uuid: participant.uuid,
      role: participant.role,
      isActivated: participant.isActivated,
      projects: [],
      createdAt: participant.createdAt,
      UserProject: {
        UserUuid: participant.uuid,
        ProjectUuid: projectId,
        share: participant.totalShare || 0,
        shareConfiguration: participant.shareConfigurations || [],
        upToDate: participant.upToDate ?? true,
      },
    })
  );
}

export function useProjectParticipants(projectId: string | undefined, options?: { enabled?: boolean }) {
  const { getWithAxios } = useAxios();

  return useQuery({
    queryKey: queryKeys.projects.participants(projectId || ''),
    queryFn: async (): Promise<User[]> => {
      if (!projectId) return [];
      const response = await getWithAxios(ADMIN_PROJECT_PARTICIPANTS_URL(projectId));
      const participants = response?.participants || [];
      return mapParticipantsToUsers(participants, projectId);
    },
    enabled: !!projectId && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
  });
}
